import { useState, useCallback, useEffect, useRef } from 'react'
import {
  Alert, Badge, Box, Button, Group, Loader, Modal, PasswordInput,
  SegmentedControl, Select, Stack, Table, Tabs, Text, TextInput, Title, Tooltip,
} from '@mantine/core'
import { AreaChart } from '@mantine/charts'
import { api } from '@/api/client'
import { useQuery } from '@tanstack/react-query'
import { CaptureCard, FlowLogsCard } from './VpnDiagnostics'
import { ResizableDrawer } from '@/components/ResizableDrawer'

// ── Tipos (espelham o payload de /inventory/vpn) ──────────────────────────
export interface VpnTunnel {
  outside_ip: string
  status: 'UP' | 'DOWN' | string
  status_message: string | null
  last_status_change: string | null
  accepted_route_count: number
}

export interface SiteToSiteVpnItem {
  vpn_connection_id: string
  name: string | null
  state: string
  customer_gateway_ip: string | null
  gateway_type: string | null
  gateway_id: string | null
  static_routes: string[]
  tunnels: VpnTunnel[]
  region: string
}

export interface ClientVpnItem {
  endpoint_id: string
  name: string | null
  state: string
  client_cidr: string | null
  auth_methods: string[]
  dns_servers: string[]
  active_connection_count: number
  region: string
}

export interface VpnApiResponse {
  status: 'ok' | 'no_vpn' | 'access_denied' | 'disabled'
  site_to_site: SiteToSiteVpnItem[]
  client_vpn: ClientVpnItem[]
}

export interface VpnClientConnection {
  connection_id: string
  common_name: string | null
  username: string | null
  client_ip: string | null
  ingress_bytes: number
  egress_bytes: number
  established_at: string | null
  status: string
}

function TunnelBadge({ tunnel }: { tunnel: VpnTunnel }) {
  const up = tunnel.status === 'UP'
  return (
    <Tooltip
      label={tunnel.status_message || (up ? 'Túnel ativo' : 'Túnel inativo')}
      position="top" withArrow
    >
      <Badge size="sm" variant="light" color={up ? 'green' : 'red'}>
        {tunnel.outside_ip} {up ? '●' : '○'} {tunnel.status}
      </Badge>
    </Tooltip>
  )
}

export function SiteToSiteTable({
  items, onSelect,
}: {
  items: SiteToSiteVpnItem[]
  onSelect: (item: SiteToSiteVpnItem) => void
}) {
  if (items.length === 0) return null
  return (
    <Box mb="lg">
      <Title order={5} mb="xs">Site-to-Site VPN ({items.length})</Title>
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Conexão</Table.Th>
            <Table.Th>Gateway</Table.Th>
            <Table.Th>CGW IP</Table.Th>
            <Table.Th>Túneis</Table.Th>
            <Table.Th>Rotas</Table.Th>
            <Table.Th>Região</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((v) => (
            <Table.Tr key={v.vpn_connection_id} onClick={() => onSelect(v)} style={{ cursor: 'pointer' }}>
              <Table.Td fw={500}>{v.name || v.vpn_connection_id}</Table.Td>
              <Table.Td>
                <Text size="xs" ff="monospace">
                  {v.gateway_type ? `${v.gateway_type.toUpperCase()} ${v.gateway_id}` : '—'}
                </Text>
              </Table.Td>
              <Table.Td><Text size="xs" ff="monospace">{v.customer_gateway_ip || '—'}</Text></Table.Td>
              <Table.Td>
                <Group gap={4}>
                  {v.tunnels.map((t) => <TunnelBadge key={t.outside_ip} tunnel={t} />)}
                </Group>
              </Table.Td>
              <Table.Td><Text size="xs">{v.static_routes.join(', ') || '—'}</Text></Table.Td>
              <Table.Td><Badge size="sm" variant="outline">{v.region}</Badge></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  )
}

export function ClientVpnTable({
  items, onSelect,
}: {
  items: ClientVpnItem[]
  onSelect: (item: ClientVpnItem) => void
}) {
  if (items.length === 0) return null
  return (
    <Box>
      <Title order={5} mb="xs">Client VPN ({items.length})</Title>
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Endpoint</Table.Th>
            <Table.Th>Estado</Table.Th>
            <Table.Th>CIDR de clientes</Table.Th>
            <Table.Th>Autenticação</Table.Th>
            <Table.Th>Conexões ativas</Table.Th>
            <Table.Th>Região</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((c) => (
            <Table.Tr key={c.endpoint_id} onClick={() => onSelect(c)} style={{ cursor: 'pointer' }}>
              <Table.Td fw={500}>{c.name || c.endpoint_id}</Table.Td>
              <Table.Td>
                <Badge size="sm" variant="light" color={c.state === 'available' ? 'green' : 'yellow'}>
                  {c.state}
                </Badge>
              </Table.Td>
              <Table.Td><Text size="xs" ff="monospace">{c.client_cidr || '—'}</Text></Table.Td>
              <Table.Td><Text size="xs">{c.auth_methods.join(', ') || '—'}</Text></Table.Td>
              <Table.Td><Badge size="sm" variant="light">{c.active_connection_count}</Badge></Table.Td>
              <Table.Td><Badge size="sm" variant="outline">{c.region}</Badge></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  )
}

export function ClientVpnConnectionsDrawer({
  endpoint, tenantId, onClose,
}: {
  endpoint: ClientVpnItem
  tenantId: number
  onClose: () => void
}) {
  const { data, isLoading, error } = useQuery<{ connections: VpnClientConnection[] }, Error>({
    queryKey: ['vpn-client-connections', tenantId, endpoint.endpoint_id],
    queryFn: async () => {
      const res = await api.get<{ connections: VpnClientConnection[] }>(
        `/v1/tenants/${tenantId}/inventory/vpn/client/${endpoint.endpoint_id}/connections`,
        { params: { region: endpoint.region } },
      )
      return res.data
    },
    retry: false,
  })
  const fmtBytes = (n: number) =>
    n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${(n / 1024).toFixed(1)} KB`
  return (
    <ResizableDrawer opened onClose={onClose} storageKey="vpn-client-conn-drawer"
      title={`Conexões ativas — ${endpoint.name || endpoint.endpoint_id}`}>
      {isLoading && <Loader size="sm" />}
      {error && <Text c="red" size="sm">Erro ao buscar conexões: {error.message}</Text>}
      {data && data.connections.length === 0 && (
        <Text size="sm" c="dimmed">Nenhuma conexão ativa neste endpoint.</Text>
      )}
      {data && data.connections.length > 0 && (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Usuário</Table.Th>
              <Table.Th>IP do cliente</Table.Th>
              <Table.Th>Entrada</Table.Th>
              <Table.Th>Saída</Table.Th>
              <Table.Th>Desde</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.connections.map((c) => (
              <Table.Tr key={c.connection_id}>
                <Table.Td fw={500}>{c.username || c.common_name || '—'}</Table.Td>
                <Table.Td><Text size="xs" ff="monospace">{c.client_ip || '—'}</Text></Table.Td>
                <Table.Td><Text size="xs">{fmtBytes(c.ingress_bytes)}</Text></Table.Td>
                <Table.Td><Text size="xs">{fmtBytes(c.egress_bytes)}</Text></Table.Td>
                <Table.Td>
                  <Text size="xs">
                    {c.established_at ? new Date(c.established_at).toLocaleString('pt-BR') : '—'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </ResizableDrawer>
  )
}

// ── Modal de exportação de configuração VPN ───────────────────────────────

interface DeviceType { id: string; vendor: string; platform: string; software: string }

export function VpnExportModal({ tenantId, vpnId, region, opened, onClose }: {
  tenantId: number; vpnId: string; region: string; opened: boolean; onClose: () => void
}) {
  const [vendor, setVendor] = useState<string | null>(null)
  const [platform, setPlatform] = useState<string | null>(null)
  const [deviceTypeId, setDeviceTypeId] = useState<string | null>(null)
  const [ike, setIke] = useState('ikev2')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [delivery, setDelivery] = useState('download')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!opened) {
      setPassword('')
      setEmail('')
      setErr(null)
    }
  }, [opened])

  const { data } = useQuery<{ device_types: DeviceType[] }, Error>({
    queryKey: ['vpn-device-types', tenantId, vpnId, region],
    queryFn: async () => (await api.get(
      `/v1/tenants/${tenantId}/inventory/vpn/${vpnId}/device-types`, { params: { region } })).data,
    enabled: opened, retry: false,
  })
  const types = data?.device_types ?? []
  const vendors = Array.from(new Set(types.map(t => t.vendor))).sort()
  const platforms = Array.from(new Set(types.filter(t => t.vendor === vendor).map(t => t.platform))).sort()
  const softwares = types.filter(t => t.vendor === vendor && t.platform === platform)

  const errMsg = (e: unknown): string => {
    const err = e as { response?: { status?: number; data?: { detail?: string } } }
    const d = err?.response?.data?.detail
    const map: Record<string, string> = {
      'password_min_8': 'A senha deve ter ao menos 8 caracteres.',
      'invalid_recipient_email': 'E-mail do parceiro inválido.',
      'region_device_ike_required': 'Selecione o equipamento e a versão de IKE.',
      'forbidden:vpn_diag:export_config': 'Você não tem permissão para exportar a configuração.',
      'vpn_connection_not_found': 'Conexão VPN não encontrada.',
    }
    if (err?.response?.status === 502) return 'Falha ao enviar o e-mail. Tente o download.'
    return (d && map[d]) || 'Erro ao exportar a configuração.'
  }

  const submit = async () => {
    setErr(null)
    if (password.length < 8) { setErr('A senha deve ter ao menos 8 caracteres.'); return }
    if (delivery === 'email' && !email.includes('@')) { setErr('Informe o e-mail do parceiro.'); return }
    if (!deviceTypeId) { setErr('Selecione o equipamento.'); return }
    setBusy(true)
    try {
      const payload = { region, device_type_id: deviceTypeId, ike_version: ike, password, delivery,
                        ...(delivery === 'email' ? { recipient_email: email } : {}) }
      if (delivery === 'download') {
        const resp = await api.post(
          `/v1/tenants/${tenantId}/inventory/vpn/${vpnId}/export`, payload, { responseType: 'blob' })
        const url = URL.createObjectURL(resp.data as Blob)
        const a = document.createElement('a'); a.href = url; a.download = `${vpnId}-config.zip`
        a.click(); URL.revokeObjectURL(url)
      } else {
        await api.post(`/v1/tenants/${tenantId}/inventory/vpn/${vpnId}/export`, payload)
      }
      onClose()
    } catch (e) {
      setErr(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Exportar / Enviar ao parceiro" size="lg">
      <Stack gap="sm">
        <Alert color="orange" title="Conteúdo sensível">
          O pacote contém a Pre-Shared Key (PSK). O ZIP e o PDF são protegidos por senha —
          envie a senha ao parceiro por outro canal (telefone/WhatsApp).
        </Alert>
        <Select label="Fabricante" data={vendors} value={vendor} searchable
                onChange={(v) => { setVendor(v); setPlatform(null); setDeviceTypeId(null) }} />
        <Select label="Plataforma" data={platforms} value={platform} searchable disabled={!vendor}
                onChange={(v) => { setPlatform(v); setDeviceTypeId(null) }} />
        <Select label="Software" disabled={!platform} value={deviceTypeId} searchable
                data={softwares.map(s => ({ value: s.id, label: s.software }))}
                onChange={setDeviceTypeId} data-testid="export-software-select" />
        <Select label="IKE" data={['ikev1', 'ikev2']} value={ike}
                onChange={(v) => setIke(v || 'ikev2')} />
        <PasswordInput label="Senha do PDF/ZIP" value={password} required
                       onChange={(e) => setPassword(e.currentTarget.value)}
                       description="Mínimo 8 caracteres. Transmita ao parceiro por outro canal." />
        <SegmentedControl value={delivery} onChange={setDelivery}
                          data={[{ label: 'Baixar (.zip)', value: 'download' },
                                 { label: 'Enviar por e-mail', value: 'email' }]} />
        {delivery === 'email' && (
          <TextInput label="E-mail do parceiro" value={email} type="email"
                     onChange={(e) => setEmail(e.currentTarget.value)} />
        )}
        {err && <Text c="red" size="sm">{err}</Text>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} loading={busy}>
            {delivery === 'email' ? 'Enviar' : 'Baixar'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

// ── Tipos de configuração VPN ─────────────────────────────────────────────

interface VpnTunnelOptions {
  options_source?: 'explicit' | 'aws_default'
  ike_versions: string[]; phase1_encryption: string[]; phase2_encryption: string[]
  phase1_integrity: string[]; phase2_integrity: string[]
  phase1_dh_groups: string[]; phase2_dh_groups: string[]
  phase1_lifetime_seconds: number | null; phase2_lifetime_seconds: number | null
  rekey_margin_time_seconds: number | null; rekey_fuzz_percentage: number | null
  replay_window_size: number | null; dpd_timeout_seconds: number | null
  dpd_timeout_action: string | null; startup_action: string | null; tunnel_inside_cidr: string | null
}

interface VpnConfigResponse {
  vpn_connection_id: string; region: string; state: string
  gateway: { type: string; id: string }
  customer_gateway: { id: string; ip_address: string | null; bgp_asn: string | null }
  routing: { type: string; static_routes: string[] }
  tunnels: { outside_ip: string; status: string; status_message: string | null
             last_status_change: string | null; options: VpnTunnelOptions }[]
}

function VpnConfigPanel({ tenantId, item }: { tenantId: number; item: SiteToSiteVpnItem }) {
  const [exportOpen, setExportOpen] = useState(false)
  const { data, isLoading, error } = useQuery<VpnConfigResponse, Error>({
    queryKey: ['vpn-config', tenantId, item.vpn_connection_id, item.region],
    queryFn: async () => (await api.get(
      `/v1/tenants/${tenantId}/inventory/vpn/${item.vpn_connection_id}/config`,
      { params: { region: item.region } })).data,
    retry: false,
  })
  if (isLoading) return <Loader size="sm" />
  if (error) return <Text c="red" size="sm">Erro ao carregar configuração: {error.message}</Text>
  if (!data) return null
  const fmt = (v: unknown) => Array.isArray(v) ? (v.join(', ') || '—') : (v === null || v === undefined || v === '' ? '—' : String(v))
  const row = (k: string, v: unknown) => (
    <Table.Tr key={k}><Table.Td fw={500}>{k}</Table.Td>
      <Table.Td><Text size="xs" ff="monospace">{fmt(v)}</Text></Table.Td></Table.Tr>
  )
  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button size="xs" variant="light" onClick={() => setExportOpen(true)}>
          Exportar / Enviar ao parceiro
        </Button>
      </Group>
      <VpnExportModal tenantId={tenantId} vpnId={item.vpn_connection_id}
        region={item.region} opened={exportOpen} onClose={() => setExportOpen(false)} />
      <Box>
        <Title order={6} mb={4}>Gateway</Title>
        <Table withTableBorder><Table.Tbody>
          {row('Tipo', data.gateway.type ? data.gateway.type.toUpperCase() : '—')}
          {row('ID', data.gateway.id)}
          {row('Customer Gateway', data.customer_gateway.id)}
          {row('CGW IP', data.customer_gateway.ip_address)}
          {row('BGP ASN', data.customer_gateway.bgp_asn)}
          {row('Roteamento', data.routing.type)}
          {row('Rotas estáticas', data.routing.static_routes)}
        </Table.Tbody></Table>
      </Box>
      {data.tunnels.map((t) => (
        <Box key={t.outside_ip}>
          <Group gap="xs" mb={4}>
            <Title order={6}>Túnel {t.outside_ip}</Title>
            <Badge size="sm" color={t.status === 'UP' ? 'green' : 'red'}>{t.status || '—'}</Badge>
            {t.options.options_source === 'aws_default' && (
              <Tooltip label="A conexão usa as opções de túnel padrão da AWS — esses parâmetros não foram customizados e refletem os valores efetivos aplicados pela AWS.">
                <Badge size="sm" variant="light" color="gray">Padrão AWS</Badge>
              </Tooltip>
            )}
          </Group>
          <Table withTableBorder><Table.Tbody>
            {row('IKE', t.options.ike_versions)}
            {row('Phase1 enc', t.options.phase1_encryption)}
            {row('Phase2 enc', t.options.phase2_encryption)}
            {row('Phase1 integridade', t.options.phase1_integrity)}
            {row('Phase2 integridade', t.options.phase2_integrity)}
            {row('Phase1 DH', t.options.phase1_dh_groups)}
            {row('Phase2 DH', t.options.phase2_dh_groups)}
            {row('Phase1 lifetime (s)', t.options.phase1_lifetime_seconds)}
            {row('Phase2 lifetime (s)', t.options.phase2_lifetime_seconds)}
            {row('Rekey margin (s)', t.options.rekey_margin_time_seconds)}
            {row('Rekey fuzz (%)', t.options.rekey_fuzz_percentage)}
            {row('Replay window', t.options.replay_window_size)}
            {row('DPD timeout (s)', t.options.dpd_timeout_seconds)}
            {row('DPD ação', t.options.dpd_timeout_action)}
            {row('Startup', t.options.startup_action)}
            {row('Inside CIDR', t.options.tunnel_inside_cidr)}
          </Table.Tbody></Table>
        </Box>
      ))}
    </Stack>
  )
}

// ── TunnelMetricsDrawer ───────────────────────────────────────────────────

interface TunnelSeriesPoint {
  t: string
  in_bytes: number | null
  out_bytes: number | null
  state: number | null
}

interface TunnelMetricsResponse {
  vpn_connection_id: string
  region: string
  period_seconds: number
  tunnels: { outside_ip: string; series: TunnelSeriesPoint[] }[]
}

/** CloudWatch VPN emite ~5min; 60s mantém "ao vivo" sem custo inútil */
const VPN_METRICS_POLL_MS = 60_000

export function TunnelMetricsDrawer({
  item, tenantId, onClose,
}: {
  item: SiteToSiteVpnItem
  tenantId: number
  onClose: () => void
}) {
  const [metrics, setMetrics] = useState<TunnelMetricsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchMetrics = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<TunnelMetricsResponse>(
        `/v1/tenants/${tenantId}/inventory/vpn/${item.vpn_connection_id}/tunnel-metrics`,
        { params: { region: item.region } },
      )
      setMetrics(res.data)
      setError(null)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err?.response?.data?.detail ?? 'Erro ao buscar métricas dos túneis')
    } finally {
      setLoading(false)
    }
  }, [tenantId, item.vpn_connection_id, item.region])

  useEffect(() => {
    fetchMetrics()
    pollRef.current = setInterval(fetchMetrics, VPN_METRICS_POLL_MS)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [fetchMetrics])

  const fmtMb = (v: number | null) => (v == null ? 0 : v / 1048576)

  return (
    <ResizableDrawer opened onClose={onClose} storageKey="vpn-conn-drawer"
      title={`VPN — ${item.name || item.vpn_connection_id}`}>
      <Tabs defaultValue="trafego">
        <Tabs.List mb="sm">
          <Tabs.Tab value="trafego">Tráfego</Tabs.Tab>
          <Tabs.Tab value="config">Configuração</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="trafego">
          <Stack gap="lg">
            <Group gap="xs">
              {item.tunnels.map((t) => <TunnelBadge key={t.outside_ip} tunnel={t} />)}
            </Group>
            {error && <Text c="red" size="sm">{error}</Text>}
            {!metrics && loading && <Loader size="sm" />}
            {metrics?.tunnels.map((tunnel) => {
              const chartData = tunnel.series.map((p) => ({
                t: new Date(p.t).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
                'Entrada (MB)': fmtMb(p.in_bytes),
                'Saída (MB)': fmtMb(p.out_bytes),
              }))
              return (
                <Box key={tunnel.outside_ip}>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={600} c="gray.7">
                      Tráfego — túnel {tunnel.outside_ip} (últimas 3h, atualiza a cada {VPN_METRICS_POLL_MS / 1000}s)
                    </Text>
                    {loading && <Loader size="xs" />}
                  </Group>
                  {chartData.length === 0 ? (
                    <Text size="xs" c="dimmed">
                      Sem dados no período (túnel sem tráfego ou métricas indisponíveis).
                    </Text>
                  ) : (
                    <AreaChart
                      h={180}
                      data={chartData}
                      dataKey="t"
                      series={[
                        { name: 'Entrada (MB)', color: 'teal.6' },
                        { name: 'Saída (MB)', color: 'indigo.6' },
                      ]}
                      curveType="natural"
                      withDots={false}
                      withLegend
                      xAxisProps={{ interval: 'preserveStartEnd' }}
                      fillOpacity={0.15}
                    />
                  )}
                </Box>
              )
            })}
            {item.static_routes.length > 0 && (
              <Box>
                <Text size="sm" fw={600} c="gray.7" mb={4}>Rotas estáticas</Text>
                <Text size="xs" ff="monospace">{item.static_routes.join('  ·  ')}</Text>
              </Box>
            )}
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="config">
          <VpnConfigPanel tenantId={tenantId} item={item} />
        </Tabs.Panel>
      </Tabs>
    </ResizableDrawer>
  )
}

export function VpnPanel({
  data, tenantId,
}: {
  data: VpnApiResponse
  tenantId: number
}) {
  const [selectedS2s, setSelectedS2s] = useState<SiteToSiteVpnItem | null>(null)
  const [selectedCvpn, setSelectedCvpn] = useState<ClientVpnItem | null>(null)

  const firstS2s = data.site_to_site[0]
  const region = firstS2s?.region ?? 'us-east-1'
  const peerCidr = firstS2s?.static_routes?.[0] ?? ''

  return (
    <Stack gap="md">
      <Tabs defaultValue="resumo">
        <Tabs.List mb="sm">
          <Tabs.Tab value="resumo">Resumo</Tabs.Tab>
          <Tabs.Tab value="diagnostico">Diagnóstico</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="resumo">
          <Stack gap="md">
            <SiteToSiteTable items={data.site_to_site} onSelect={setSelectedS2s} />
            <ClientVpnTable items={data.client_vpn} onSelect={setSelectedCvpn} />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="diagnostico">
          <FlowLogsCard
            tenantId={tenantId}
            region={region}
            vpcIds={[]}
            peerCidr={peerCidr}
          />
          {/* hasPci=false: o backend é a autoridade final e bloqueia full_payload server-side
              via 422. Quando o campo PCI estiver disponível no objeto tenant, passar aqui. */}
          <CaptureCard
            tenantId={tenantId}
            region={region}
            peerCidr={peerCidr}
            hasPci={false}
          />
        </Tabs.Panel>
      </Tabs>

      {selectedS2s && (
        <TunnelMetricsDrawer item={selectedS2s} tenantId={tenantId} onClose={() => setSelectedS2s(null)} />
      )}
      {selectedCvpn && (
        <ClientVpnConnectionsDrawer endpoint={selectedCvpn} tenantId={tenantId} onClose={() => setSelectedCvpn(null)} />
      )}
    </Stack>
  )
}
